/**
 * CR308/CR305: PointsValueBoxBlock
 * 
 * Specialized value display for POINTS deals.
 * Shows:
 * - Points Earned (e.g., "15,000 PC Optimum")
 * - Cash Equivalent (e.g., "≈ $15.00 CAD") when conversion rate is available
 * - Microcopy line with conversion rate info
 * 
 * This component is used internally by ValueBoxBlock for POINTS deals.
 */

import type { FC } from "react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { cn } from "@/lib/utils";

/**
 * Get points display values from deal.
 */
function getPointsDisplayValues(deal: Deal): {
  pointsAmount: number | null;
  pointsProgramName: string | null;
  cashValue: number | null;
  conversionRate: number | null;
  spendThreshold: number | null;
  effectivePercentBack: number | null;
} {
  // Extract points amount from various sources
  const pointsAmount = deal.points_amount ?? null;
  
  // Get program name
  const pointsProgramName = deal.points_program_name ?? null;
  
  // Get conversion rate and compute cash value
  const conversionRate = deal.points_to_currency_rate ?? null;
  
  let cashValue: number | null = null;
  if (pointsAmount && conversionRate) {
    cashValue = pointsAmount * conversionRate;
  }
  
  // Also check if computed_value_cad is already set
  if (!cashValue && deal.computed_value_cad) {
    cashValue = deal.computed_value_cad;
  }
  
  // Spend threshold for "per $X spent"
  const spendThreshold = deal.spend_amount ?? 
                        deal.qualifying_spend_cad ?? 
                        (deal as any).spend_threshold_amount ?? 
                        null;
  
  // Compute effective percent back if we have both cash value and spend
  let effectivePercentBack: number | null = null;
  if (cashValue && spendThreshold) {
    effectivePercentBack = (cashValue / spendThreshold) * 100;
  } else if (deal.effective_percent_back) {
    effectivePercentBack = deal.effective_percent_back;
  }
  
  return {
    pointsAmount,
    pointsProgramName,
    cashValue,
    conversionRate,
    spendThreshold,
    effectivePercentBack,
  };
}

/**
 * Format points amount with proper locale formatting.
 */
function formatPoints(amount: number): string {
  return amount.toLocaleString();
}

/**
 * Format cash value with proper currency display.
 */
function formatCashValue(amount: number): string {
  if (amount >= 10) {
    return `$${Math.round(amount).toLocaleString()}`;
  }
  return `$${amount.toFixed(2)}`;
}

// CR305: Extended props to support DealCardBlockProps interface
export interface PointsValueBoxBlockProps extends Partial<DealCardBlockProps<Deal>> {
  deal: Deal;
  className?: string;
}

export const PointsValueBoxBlock: FC<PointsValueBoxBlockProps> = ({ deal, block, context, className }) => {
  const {
    pointsAmount,
    pointsProgramName,
    cashValue,
    conversionRate,
    spendThreshold,
    effectivePercentBack,
  } = getPointsDisplayValues(deal);
  
  // If no points data, return null
  if (!pointsAmount && !cashValue) {
    return null;
  }
  
  return (
    <div 
      className={cn("space-y-1 py-2 px-3 bg-purple-50 rounded-md border border-purple-200", className)}
      data-block="value_box"
      data-block-id={block?.id}
    >
      {/* Points Earned Line */}
      {pointsAmount && (
        <div className="flex items-baseline gap-1.5">
          <span className="text-lg font-bold text-purple-700">
            {formatPoints(pointsAmount)} pts
          </span>
          {pointsProgramName && (
            <span className="text-sm text-purple-600">
              {pointsProgramName}
            </span>
          )}
        </div>
      )}
      
      {/* Cash Equivalent Line */}
      {cashValue && (
        <p className="text-sm text-purple-700 font-semibold">
          ≈ {formatCashValue(cashValue)} CAD
          {effectivePercentBack != null && spendThreshold && (
            <span className="text-purple-600 font-normal">
              {' '}• {Math.round(effectivePercentBack)}% back
            </span>
          )}
        </p>
      )}
      
      {/* No Cash Value Fallback */}
      {!cashValue && pointsAmount && conversionRate === null && (
        <p className="text-xs text-purple-500 italic">
          Cash value not available — conversion rate not defined
        </p>
      )}
      
      {/* Conversion Rate Microcopy (optional, for debug/admin) */}
      {conversionRate && cashValue && (
        <p className="text-[10px] text-purple-500/70">
          Rate: {conversionRate} CAD/pt
        </p>
      )}
    </div>
  );
};

export default PointsValueBoxBlock;
